import React, { useRef } from 'react';
import { isWeddingFunction, MAX_NAME } from '../../utils/helpers';

// Desktop "Quick add": an inline grid above the guest list — one line per
// guest, one checkbox column per open function. Enter jumps to the same box on
// the next line (adding a line at the end), so a whole list is typed without
// the mouse. All logic is in useQuickAdd.
export default function QuickAddDesktop({ vm }) {
  const {
    quickRows, quickFunctions, quickSaving, quickProgress, filledCount, closeQuick,
    setQuickField, toggleQuickFunction, toggleQuickColumn, columnState, addQuickRows, removeQuickRow, saveQuick,
  } = vm.quick;
  const gridRef = useRef(null);

  const focusCell = (row, col) => {
    const el = gridRef.current?.querySelector(`[data-q="${row}-${col}"]`);
    if (el) { el.focus(); el.select?.(); }
  };
  const onKey = (e, row, col) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    if (row + 1 >= quickRows.length) {
      addQuickRows(1);
      setTimeout(() => focusCell(row + 1, col), 0);
    } else focusCell(row + 1, col);
  };
  const cell = (r, i, field, col, extra) => (
    <input data-q={`${i}-${col}`} value={r[field]} disabled={quickSaving}
      onChange={e => setQuickField(r.key, field, e.target.value)} onKeyDown={e => onKey(e, i, col)} {...extra} />
  );

  return (
    <div className="card quick-add" ref={gridRef}>
      <div className="quick-add-head">
        <div>
          <strong>Quick add guests</strong>
          <span className="muted"> · Enter = next line · new lines copy the ticks above · ticking any function also ticks the Wedding</span>
        </div>
        <button type="button" className="icon-btn" title="Close quick add" onClick={closeQuick} disabled={quickSaving}>✕</button>
      </div>

      <div className="quick-add-scroll">
        <table className="tbl compact quick-add-table">
          <thead>
            <tr>
              <th className="num">#</th>
              <th>Surname</th>
              <th>Name *</th>
              <th>Mobile</th>
              {quickFunctions.map(f => {
                const state = columnState(f.FunctionId);
                return (
                  <th key={f.FunctionId} className="fn-col" title={`Tick / clear "${f.FunctionName}" for every line`}>
                    <label>
                      <input type="checkbox" checked={state === 'all'} disabled={quickSaving}
                        ref={el => { if (el) el.indeterminate = state === 'some'; }}
                        onChange={() => toggleQuickColumn(f.FunctionId)} />
                      <span>{isWeddingFunction(f) ? '💍 ' : ''}{f.FunctionName} <span className="fn-count">({vm.fnCount(f.FunctionId)})</span></span>
                    </label>
                  </th>
                );
              })}
              <th></th>
            </tr>
          </thead>
          <tbody>
            {quickRows.map((r, i) => (
              <React.Fragment key={r.key}>
                <tr className={r.error ? 'has-error' : undefined}>
                  <td className="num muted">{i + 1}</td>
                  <td>{cell(r, i, 'Surname', 0, { placeholder: 'Surname', maxLength: MAX_NAME })}</td>
                  <td>{cell(r, i, 'GuestName', 1, { placeholder: 'Name', maxLength: MAX_NAME })}</td>
                  <td>{cell(r, i, 'MobileNumber', 2, { placeholder: '10 digits (optional)', inputMode: 'numeric', className: 'mobile' })}</td>
                  {quickFunctions.map(f => (
                    <td key={f.FunctionId} className="fn-col">
                      <input type="checkbox" disabled={quickSaving} title={f.FunctionName}
                        checked={r.FunctionIds.includes(String(f.FunctionId))}
                        onChange={() => toggleQuickFunction(r.key, f.FunctionId)} />
                    </td>
                  ))}
                  <td>
                    <button type="button" className="icon-btn danger" title="Remove this line" tabIndex={-1}
                      disabled={quickSaving || quickRows.length === 1} onClick={() => removeQuickRow(r.key)}>✕</button>
                  </td>
                </tr>
                {r.error && (
                  <tr className="quick-add-error"><td></td><td colSpan={quickFunctions.length + 4}>⚠ {r.error}</td></tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <div className="quick-add-foot">
        <button type="button" className="btn light sm" onClick={() => addQuickRows(1)} disabled={quickSaving}>+ 1 line</button>
        <button type="button" className="btn light sm" onClick={() => addQuickRows(5)} disabled={quickSaving}>+ 5 lines</button>
        <div className="spacer" />
        {quickSaving && <span className="muted">Saving {quickProgress.done} / {quickProgress.total}…</span>}
        <button type="button" className="btn primary" data-save="primary" title="Ctrl+S" onClick={saveQuick} disabled={quickSaving || !filledCount}>
          {quickSaving ? 'Saving…' : `Save ${filledCount || ''} guest${filledCount === 1 ? '' : 's'}`}
        </button>
      </div>
    </div>
  );
}
